import React, {useState, useEffect } from "react";
import Layout from "../../layouts/Layout";
import { getOrderAPI, refundOrderAPI } from "../../services/api/OrderAPI";
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from "react-redux";
import Swal from 'sweetalert2';
import { Modal } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Loader from "../components/Loader";

const OrderDetailsPage = () => {
    const { id } = useParams();
    const user = useSelector((state) => state.LoginReducer.authData);
    const token = user.data.token;
    const navigate = useNavigate();
    const [order, setOrder] = useState({});
    const [products, setProducts] = useState([]);
    const [otherDetail, setOtherDetail] = useState({});
    const [orderSummary, setOrderSummary] = useState({});
    const [orderRefund, setOrderRefund] = useState({});
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);

    const handleShow = (data) => {
        setShow(true);
    };

    const handleClose = () => {
        setShow(false);
    };

    const validationSchema = Yup.object({
        amount: Yup.number()
            .required('Amount is required')
            .typeError('Amount must be a number')
            .positive('Amount must be greater than zero'),
        reason: Yup.string()
            .max(100, "Reason cannot exceed 100 characters")
    });

    const formik = useFormik({
        initialValues: {
            amount: '',
            reason: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('amount', values.amount);
            formData.append('order_id', order.id);
            await refundOrder(formData);
            setLoading(false);
        },
    });

    const refundOrder = async (formData) => {
        try {            
            const data = await refundOrderAPI(formData, token);
            setLoading(false);
            if (data.status) {
                setShow(false);
                Swal.fire({
                    title: 'Refund successful!',
                    text: 'Refund successful.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    setReload(!reload);
                });
            }else{
                Swal.fire({
                    title: 'Error!',
                    text: data.error,
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const data = await getOrderAPI(id, token);
                setLoading(false);
                if (data === null) {
                    navigate('/404');
                } else {
                    setOrder(data);
                    setProducts(data.products);
                    setOtherDetail(data.other_detail);
                    setOrderSummary(data.summary);
                    setOrderRefund(data.refund);
                }
            } catch (error) {
                setLoading(false);
                navigate('/orders');
                Swal.fire({
                    title: 'Error!',
                    text: 'Could not fetch order data.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        };
        fetchOrder();        
    }, [id, reload]);

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout title="Order Details">
            <div className="container table-container">
                <div className="card">
                    <div className="card-header">
                    {order.order_id && (
                        <p className="mb-0 text-muted">Order Number: {order.order_id}</p>
                    )}
                    {order.order_date && (
                        <p className="mb-0 text-muted">Date: {order.order_date}</p>
                    )}
                    </div>
                    <div className="card-body">
                        <div className="row">
                            {order.customer && (
                                <div className="col-12 col-md-6">
                                    <h3>Customer Information</h3>
                                    {order.customer.name && (
                                        <p><strong>Name:</strong>{order.customer.name}</p>
                                    )}
                                    {order.customer.email && (
                                        <p><strong>Email:</strong> {order.customer.email}</p>
                                    )}
                                    {order.customer.phone && (
                                        <p><strong>Phone:</strong> {order.customer.phone}</p>
                                    )}
                                </div>
                            )}
                            <div className="col-12 col-md-6">
                                <div className="row">
                                    {(order.billing) && (
                                        <div className="col-12 col-md-12">
                                            <h3>Billing Details</h3>
                                            <p><strong>Name:</strong> { order.billing.name ? order.billing.name : ''}</p>
                                            <p><strong>Address:</strong> {order.billing.address ? order.billing.address : ''}</p>
                                        </div>
                                    )}
                                    
                                    {order.shipping.shipping_details_id &&
                                        <>
                                            <div className="col-12 col-md-12">
                                                <h3>Shipping Details</h3>
                                                <p><strong>Name:</strong> {order.shipping.name ? order.shipping.name : ''}</p>
                                                <p><strong>Address:</strong> {order.shipping.address ? order.shipping.address : ''}</p>
                                            </div>
                                        </>
                                    }
                                </div>
                            </div>

                            <div className="col-12">
                                <h3>Products</h3>
                                {products && products.map((item, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center border-bottom py-2">
                                        <div className="d-flex align-items-center">
                                            <img src={item.image} alt={item.product_name} style={{ width: "50px", height: "50px", marginRight: "15px" }} />
                                            <div>
                                                <p className="mb-0"><strong>{item.product_name}</strong></p>
                                                <p className="mb-0 text-muted">Price: ${item.price.toFixed(2)}</p>
                                                <p className="mb-0 text-muted">Quantity: {item.quantity}</p>
                                            </div>
                                        </div>
                                        <p className="mb-0"><strong>Total:</strong> ${(item.price * item.quantity).toFixed(2)}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Other Details */}
                            <div className="col-12 col-sm-6  mt-4">
                                <h3>Other Details</h3>
                                <p><strong>Payment Method:</strong> {otherDetail.payment_method}</p>
                                <p><strong>Transaction ID:</strong> {otherDetail.transaction_id}</p>
                                {otherDetail.order_notes && 
                                    <p><strong>Notes:</strong> {otherDetail.order_notes}</p>
                                }
                                {order.status === "Return" && (
                                    <>
                                        <p><strong>Reason of Return:</strong> {order.reason}</p>
                                        {order.return_status === "Pending" && (
                                            <div className="d-flex justify-content-end align-items-center">
                                                <button className="btn btn-dark" onClick={() => handleShow(order)}>Refund</button>
                                            </div>
                                        )}
                                    </>
                                )}

                                {orderRefund && (
                                    <>
                                        <h3>Refund Details</h3>
                                        <p><strong>Refund Transaction ID:</strong> {orderRefund.capture_id}</p>
                                        <p><strong>Refund Status:</strong> {orderRefund.status}</p>
                                    </>
                                )}

                            </div>

                            {/* Order Summary */}
                            <div className="col-12 col-sm-6 col-md-6 mt-4">
                                <h3>Order Summary</h3>
                                <div className="border p-3 rounded">
                                    <ul className="list-unstyled">
                                    <li className="d-flex justify-content-between">
                                        <span>Subtotal:</span>
                                        <span>${parseFloat(orderSummary.sub_total).toFixed(2)}</span>
                                    </li>
                                    <li className="d-flex justify-content-between">
                                        <span>Sales Tax:</span>
                                        <span>${parseFloat(orderSummary.tax).toFixed(2)}</span>
                                    </li>
                                    <li className="d-flex justify-content-between">
                                        <span>Shipping:</span>
                                        <span>${parseFloat(orderSummary.shipping_cost).toFixed(2)}</span>
                                    </li>
                                    <li className="d-flex justify-content-between">
                                        <span>Gift Certificate:</span>
                                        <span>-${parseFloat(orderSummary.total_discount).toFixed(2)}</span>
                                    </li>
                                    <li className="d-flex justify-content-between border-top pt-2">
                                        <strong>Total:</strong>
                                        <strong>${parseFloat(orderSummary.total).toFixed(2)}</strong>
                                    </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Modal */}
            <Modal show={show} onHide={handleClose} size="md" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Refund Order Amount</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                {order && (
                    <form onSubmit={formik.handleSubmit}>
                        <input type="hidden" name="order_id" value={order.order_id} />
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12 text-align-center">
                                    <h3>Order Id: {order.order_id}</h3>
                                </div>
                                <div className="col-md-12 mt-3">
                                    <label htmlFor="amount">Amount</label>
                                    <input type="text" name="amount" id="amount" className="form-control" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.amount} />
                                    {formik.touched.amount && formik.errors.amount ? (<label className="text-danger">{formik.errors.amount}</label>) : null}
                                </div>
                                <div className="col-md-12 mt-3">
                                    <label htmlFor="reason">Reason for refund </label>
                                    <input type="text" name="reason" id="reason" className="form-control" onChange={formik.handleChange} onBlur={formik.handleBlur} value={order.reason ? order.reason : formik.values.reason} />
                                    {formik.touched.reason && formik.errors.reason ? (<label className="text-danger">{formik.errors.reason}</label>) : null}
                                </div>
                                <div className="col-md-12 mt-3 d-flex justify-content-center">
                                    <button type="submit" className="btn btn-primary">Refund</button>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
                </Modal.Body>
            </Modal>
        </Layout>
    );
};

export default OrderDetailsPage;
